import { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { SettingsList, SettingsItem, SettingsStatGrid, StatusBadge } from '../components/ui/SettingsItem';
import { API_BASE_URL } from '../api';
import { BookOpen, CircleHelp, FileDown, FileText, School, Wifi, Smartphone, Languages, Palette, RefreshCw, Trash2, Calendar } from 'lucide-react';
import { DELETE_DATA_URL, getHelpUrl, HELP_PDF_URL, PRIVACY_URL, TERMS_URL } from '../constants/contact';
import { openExternalUrl } from '../utils/openExternalUrl';
import { formatSubscriptionDate, isSubscriptionExpired, daysUntilExpiry } from '../utils/academicYear';
import { ThemeToggle } from '../components/ui/ThemeToggle';

export function TeacherSettings() {
  const { labels, userInfo, strengthForm, useMalayalam, setUseMalayalam, online, pending } = useApp();
  const subscriptionExpires = userInfo?.subscription_expires_at;
  const [apiStatus, setApiStatus] = useState('checking');

  const apiOnline = apiStatus === 'online';
  const currentLanguage = useMalayalam ? labels.langMalayalam : labels.langEnglish;

  const checkApi = async () => {
    setApiStatus('checking');
    try {
      const res = await fetch(`${API_BASE_URL}/health`, { method: 'GET' });
      setApiStatus(res.ok ? 'online' : 'error');
    } catch {
      setApiStatus('offline');
    }
  };

  useEffect(() => {
    checkApi();
  }, []);

  return (
    <div className="space-y-4 pb-8">
      <PageHeader title={labels.teacherSettingsTitle} subtitle={labels.teacherSettingsSubtitle} />

      <SettingsList>
        <SettingsStatGrid
          items={[
            { label: labels.division, value: strengthForm.division_name || '—' },
            { label: labels.maxBoys, value: strengthForm.max_boys !== '' && strengthForm.max_boys != null ? strengthForm.max_boys : '—' },
            { label: labels.maxGirls, value: strengthForm.max_girls !== '' && strengthForm.max_girls != null ? strengthForm.max_girls : '—' },
          ]}
        />

        <SettingsItem
          icon={Wifi}
          title={labels.apiConnection}
          action={
            apiStatus === 'checking' ? (
              <span className="text-xs text-muted">{labels.loading}</span>
            ) : (
              <StatusBadge online={apiOnline} labels={labels} />
            )
          }
        />

        <SettingsItem
          icon={Smartphone}
          title={labels.deviceNetwork}
          subtitle={
            !online && pending > 0
              ? labels.pendingSyncCount.replace('{n}', String(pending))
              : undefined
          }
          action={<StatusBadge online={online} labels={labels} />}
        />

        {subscriptionExpires && (
          <SettingsItem
            icon={Calendar}
            title={labels.subscriptionValidUntil}
            subtitle={
              isSubscriptionExpired(subscriptionExpires)
                ? labels.subscriptionExpired
                : (() => {
                    const d = daysUntilExpiry(subscriptionExpires);
                    return d !== null && d <= 14
                      ? `${labels.subscriptionExpiringSoon} (${d}d)`
                      : formatSubscriptionDate(subscriptionExpires);
                  })()
            }
          />
        )}

        <SettingsItem
          icon={Palette}
          title={labels.appearance}
          subtitle={labels.appearanceHint}
          action={<ThemeToggle useMalayalam={useMalayalam} />}
        />

        <SettingsItem
          icon={Languages}
          title={labels.appLanguage}
          subtitle={currentLanguage}
          action={
            <Button variant="secondary" size="sm" onClick={() => setUseMalayalam(!useMalayalam)}>
              {labels.toggleLang}
            </Button>
          }
        />

        {userInfo?.name && (
          <SettingsItem
            icon={School}
            title={labels.signedInAs}
            subtitle={userInfo.name}
          />
        )}

        <SettingsItem
          icon={RefreshCw}
          title={labels.refresh}
          subtitle={labels.refreshStatusHint}
          action={
            <Button variant="secondary" size="sm" onClick={checkApi}>
              {labels.refresh}
            </Button>
          }
        />

        <SettingsItem
          icon={CircleHelp}
          title={labels.helpLink}
          subtitle={labels.helpLinkHint}
          onClick={() => openExternalUrl(getHelpUrl(useMalayalam))}
        />

        <SettingsItem
          icon={FileDown}
          title={labels.helpPdfDownload}
          subtitle={labels.helpPdfDownloadHint}
          onClick={() => openExternalUrl(HELP_PDF_URL)}
        />

        <SettingsItem
          icon={FileText}
          title={labels.termsLink}
          onClick={() => openExternalUrl(TERMS_URL)}
        />

        <SettingsItem
          icon={BookOpen}
          title={labels.privacyLink}
          onClick={() => openExternalUrl(PRIVACY_URL)}
        />

        <SettingsItem
          icon={Trash2}
          title={labels.deleteDataLink}
          subtitle={labels.deleteDataHint}
          onClick={() => openExternalUrl(DELETE_DATA_URL)}
        />
      </SettingsList>
    </div>
  );
}
